import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useStore } from "@tanstack/react-form";
import {
  getFormDefaultValues,
  createPositionSchema,
} from "../../ElementInspectorV3/utils";
import { useAppForm } from "../../ElementInspectorV3/elementInspectorForm/form";
import type { UseSelectedElementEditingArgs } from "./types";

export const useSelectedElementEditing = ({
  selectedElement,
  onSubmit,
}: UseSelectedElementEditingArgs) => {
  const { t } = useTranslation();

  const positionSchema = useMemo(
    () =>
      createPositionSchema({
        bbox: t("smartSurveyor.validation.positionRequired"),
        groupId: t("smartSurveyor.validation.elementTypeRequired"),
        multiplier: t("smartSurveyor.validation.multiplierMin"),
      }),
    [t],
  );

  const selectedElementForm = useAppForm({
    defaultValues: getFormDefaultValues(selectedElement),
    validators: { onBlur: positionSchema },
    onSubmit,
    onSubmitInvalid: () => {
      toast.error(t("smartSurveyor.elementDetails.form.validationFailed"));
    },
  });

  const formBbox = useStore(selectedElementForm.store, (state) => ({
    isDirty: state.isDirty,
    bbox: state.values.bbox,
  }));

  const draftSelectedElement = useMemo(() => {
    if (!selectedElement) return null;
    if (!formBbox.isDirty) return selectedElement;
    return { ...selectedElement, bbox: formBbox.bbox };
  }, [selectedElement, formBbox]);

  return { selectedElementForm, draftSelectedElement };
};
